import { API_URL } from '@/constants/config';
import { cookieKey, getAuthHeader } from '~/modules/common/utils/cookieUtils';
import { encodeQuery } from '~/modules/common/utils/routingUtils';

const base = API_URL + '/v3.5';

/**
 * Auth Service
 *
 * @param {object} http axios instance from nuxt context
 * @param {object} cookies cookies from nuxt context
 */
export const authService = (http, cookies) => ({
  /**
   * Get Auth(access) Token
   *
   * @param {object} params
   * @param {string} params.email email
   * @param {string} params.password password
   */
  async getAuthToken({ email, password }) {
    const body = {
      grant_type: 'password',
      id: email,
      password,
    };
    const { data } = await http.post(`${base}/auth/login?authVersion=v2`, body);
    return data;
  },

  /**
   * Get token with fb
   *
   * @param {string} token token
   */
  async getAuthTokenGoogle(token) {
    const body = {
      token,
    };
    const { data: result } = await http.post(`${base}/auth/google/login?authVersion=v2`, body);
    return { data: result.data };
  },

  /**
   * Get token with fb
   *
   * @param {string} token fbtoken
   */
  async getAuthTokenFB(token) {
    const body = {
      accessToken: token,
    };
    const { data: result } = await http.post(`${base}/auth/facebook/login?authVersion=v2`, body);
    return { data: result.data };
  },

  /**
   * Get token with twitter
   *
   * @param {string} oauthToken token
   * @param {string} oauthTokenSecret token secret
   * @param {string} oauthVerifier verifier
   */
  async getAuthTokenTwitter(oauthToken, oauthTokenSecret, oauthVerifier) {
    const body = {
      oauthToken,
      oauthTokenSecret,
      oauthVerifier,
    };
    const { data: result } = await http.post(`${base}/auth/twitter/login?authVersion=v2`, body);
    return { data: result.data };
  },

  /**
   * Get twitter request token
   */
  async getRequestTokenTwitter() {
    const { data: result } = await http.get(`${base}/auth/twitter/request-token`);
    return result;
  },

  /**
   * Refresh token
   */
  async refreshToken() {
    const refreshToken = cookies.get(cookieKey.REFRESH_TOKEN);
    const { data: result } = await http.post(`${base}/auth/token?authVersion=v2`, { refreshToken });
    return result;
  },

  /**
   * Get Current User
   */
  async getCurrentUser() {
    const { data: result } = await http.get(`${base}/users/me/profile`, {
      headers: { ...getAuthHeader(cookies) },
    });
    return { data: result.data };
  },

  /**
   * Register
   *
   * @param {string} user.email
   * @param {string} user.username
   */
  async register({ email, password }) {
    const body = {
      version: 'v2',
      email,
      password,
    };
    const { data: result } = await http.post(`${base}/auth/register?authVersion=v2`, body);
    return result;
  },

  /**
   * Check username
   *
   * @param {string} username
   */
  async checkUsername(username) {
    const { data: result } = await http.get(`${base}/auth/check/username${encodeQuery({ username })}`, {
      headers: { ...getAuthHeader(cookies) },
    });
    return result;
  },

  /**
   * Verify user by email
   *
   * @param {string} token - verify token
   */
  async verifyEmail(token) {
    const queryParams = encodeQuery({ token });
    const { data } = await http.get(`${base}/auth/verify-email${queryParams}`);
    return { data };
  },

  /**
   * Send verify email
   */
  async sendVerifyEmail() {
    const data = await http.get(`${base}/auth/verify-email/new`, {
      headers: { ...getAuthHeader(cookies) },
    });
    return data;
  },

  /**
   * Forgot password mail - send reset email
   *
   * @param {string} params.id username or email
   */
  async forgotPasswordMail({ id }) {
    const body = {
      id,
    };

    const { data: result } = await http.post(`${base}/auth/reset-password/new`, body);
    return result;
  },

  /**
   * Reset password - returns temporary password
   *
   * @param {string} token - token
   * @param {string} password - password
   */
  async resetPassword({ token, password }) {
    const params = { token, password };
    const { data: result } = await http.post(`${base}/auth/reset-password`, params);
    return { data: result.data };
  },

  /**
   * Send delete account mail to user
   *
   * @param {string} feedback - reason
   */
  async deleteAccountMail(feedback) {
    const { data: result } = await http.post(
      `${base}/users/me/request-delete`,
      { feedback },
      {
        headers: { ...getAuthHeader(cookies) },
      }
    );
    return { data: result.data };
  },

  /**
   * Delete account
   *
   * @param {string} token - token
   */
  async deleteAccount(token) {
    const { data } = await http.$put(
      `${base}/users/me/delete`,
      { token },
      {
        headers: { ...getAuthHeader(cookies) },
      }
    );
    return { data };
  },

  /**
   * Deactivate account
   *
   * @param {string} feedback - reason
   */
  async deactivateAccount(feedback) {
    const { data: result } = await http.put(
      `${base}/users/me/freeze`,
      { feedback },
      {
        headers: { ...getAuthHeader(cookies) },
      }
    );
    return { data: result.data };
  },
});
